import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { refusalMessage } from '../../refusal';
import { sentence } from '../person-hub/said';

/**
 * What a list-level editor saved whole under `If-Match` was told (SR-231): the scoring-input
 * template and the territory vocabulary are both replaced whole, both refused by position with a
 * 400 and with `revision-conflict` when somebody saved first — so they say it the same way.
 */
export type WholeSaid =
  | { kind: 'saved'; text: string }
  | { kind: 'refused'; text: string }
  | { kind: 'conflict' };

/** A whole save's refusal in words — or the conflict, which the footer offers Reload for. */
export function wholeSaveRefusal(failure: unknown, fallback: string): WholeSaid {
  return failure instanceof ApiError && failure.isProblem(ProblemType.revisionConflict)
    ? { kind: 'conflict' }
    : { kind: 'refused', text: sentence(refusalMessage(failure, fallback)) };
}

/** The editor's foot: what the save was told, Save and Discard, and why a reader without the
 *  permission cannot. `what` names the thing saved ("this template", "these territories"), `it`
 *  the pronoun for it. */
export function WholeSaveFooter({
  what,
  it,
  saveLabel,
  notPermitted,
  said,
  busy,
  editable,
  dirty,
  permitted,
  onSave,
  onDiscard,
  onReload
}: {
  what: string;
  it: string;
  saveLabel: string;
  notPermitted: string;
  said: Nullable<WholeSaid>;
  busy: boolean;
  editable: boolean;
  dirty: boolean;
  permitted: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onReload: () => void;
}) {
  const reasonId = React.useId();
  return (
    <>
      {said?.kind === 'conflict' && (
        <Alert variant="destructive">
          <AlertTitle>Someone saved {what} first</AlertTitle>
          <AlertDescription>
            <span>
              Nothing was saved. Reload {it} — your changes here are dropped — and make them again.
            </span>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={onReload}>
              Reload
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {said?.kind === 'refused' && (
        <Alert variant="destructive">
          <AlertTitle>Not saved</AlertTitle>
          <AlertDescription>{said.text}</AlertDescription>
        </Alert>
      )}
      <p
        role="status"
        className={said?.kind === 'saved' ? 'text-sm text-[var(--color-teal)]' : 'sr-only'}
      >
        {said?.kind === 'saved' ? said.text : ''}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!editable || !dirty}
          aria-describedby={permitted ? undefined : reasonId}
          onClick={onSave}
        >
          {busy ? 'Saving…' : saveLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!editable || !dirty}
          onClick={onDiscard}
        >
          Discard changes
        </Button>
      </div>
      {!permitted && <NotPermitted id={reasonId} what={notPermitted} />}
    </>
  );
}
